import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import type { Musteri, MusteriFormData, IlkServisFormData, ILK_SERVIS_BOS } from '../hooks/useCustomers';
import { ISLEM_TURLERI, ODEME_DURUMLARI, ODEME_TIPLERI } from '../hooks/useCustomers';
import { telefonGoster } from '@/pages/bakim-takip/utils/bakimHesapla';

interface CustomerModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: MusteriFormData, servisData: IlkServisFormData) => Promise<{ ok: boolean; newId?: string; servisError?: string } | undefined>;
  editData?: Musteri | null;
  saving: boolean;
}

const illerData = ['Adana','Adıyaman','Afyonkarahisar','Ağrı','Amasya','Ankara','Antalya','Artvin','Aydın','Balıkesir','Bilecik','Bingöl','Bitlis','Bolu','Burdur','Bursa','Çanakkale','Çankırı','Çorum','Denizli','Diyarbakır','Edirne','Elazığ','Erzincan','Erzurum','Eskişehir','Gaziantep','Giresun','Gümüşhane','Hakkari','Hatay','Isparta','Mersin','İstanbul','İzmir','Kars','Kastamonu','Kayseri','Kırklareli','Kırşehir','Kocaeli','Konya','Kütahya','Malatya','Manisa','Kahramanmaraş','Mardin','Muğla','Muş','Nevşehir','Niğde','Ordu','Rize','Sakarya','Samsun','Siirt','Sinop','Sivas','Tekirdağ','Tokat','Trabzon','Tunceli','Şanlıurfa','Uşak','Van','Yozgat','Zonguldak','Aksaray','Bayburt','Karaman','Kırıkkale','Batman','Şırnak','Bartın','Ardahan','Iğdır','Yalova','Karabük','Kilis','Osmaniye','Düzce'];

const musteriDurumlari = ['Aktif Müşteri', 'Pasif Müşteri', 'Potansiyel Müşteri', 'VIP Müşteri'];

const empty: MusteriFormData = {
  ad: '',
  soyad: '',
  telefon: '',
  alternatif_telefon: '',
  acik_adres: '',
  il: '',
  ilce: '',
  adres_tarifi: '',
  google_maps_url: '',
  musteri_durumu: 'Aktif Müşteri',
  genel_notlar: '',
};

const emptyServis: IlkServisFormData = {
  islem_kaydi_olustur: true,
  islem_turu: 'Filtre Bakımı – Dörtlü',
  diger_aciklama: '',
  servis_tarihi: new Date().toISOString().split('T')[0],
  servis_saati: '09:00',
  tahmini_ucret: '',
  odeme_durumu: 'odendi',
  odeme_tipi: 'Nakit',
  personel_id: '',
  islem_aciklamasi: '',
  servis_notu: '',
};

const saatler = ['08:00','08:30','09:00','09:30','10:00','10:30','11:00','11:30','12:00','12:30','13:00','13:30','14:00','14:30','15:00','15:30','16:00','16:30','17:00','17:30','18:00'];

export default function CustomerModal({ open, onClose, onSave, editData, saving }: CustomerModalProps) {
  const [form, setForm] = useState<MusteriFormData>(empty);
  const [servisForm, setServisForm] = useState<IlkServisFormData>(emptyServis);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [personeller, setPersoneller] = useState<{ id: string; ad: string; soyad: string }[]>([]);

  useEffect(() => {
    if (open) {
      supabase.from('sadem_personeller').select('id, ad, soyad').eq('aktif', true).order('ad').then(({ data }) => {
        if (data) setPersoneller(data);
      }).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (editData) {
      setForm({
        ad: editData.ad || '',
        soyad: editData.soyad || '',
        telefon: telefonGoster(editData.telefon || ''),
        alternatif_telefon: editData.alternatif_telefon ? telefonGoster(editData.alternatif_telefon) : '',
        acik_adres: editData.acik_adres || '',
        il: editData.il || '',
        ilce: editData.ilce || '',
        adres_tarifi: editData.adres_tarifi || '',
        google_maps_url: editData.google_maps_url || '',
        musteri_durumu: editData.musteri_durumu || 'Aktif Müşteri',
        genel_notlar: editData.genel_notlar || '',
      });
      setServisForm({ ...emptyServis, islem_kaydi_olustur: false });
    } else {
      setForm(empty);
      setServisForm({ ...emptyServis, islem_kaydi_olustur: true });
    }
    setErrors({});
  }, [editData, open]);

  if (!open) return null;

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.ad.trim()) errs.ad = 'Ad zorunludur';
    if (!form.soyad.trim()) errs.soyad = 'Soyad zorunludur';
    if (!form.telefon.trim()) errs.telefon = 'Telefon zorunludur';
    if (!form.acik_adres.trim()) errs.acik_adres = 'Açık adres zorunludur';

    if (servisForm.islem_kaydi_olustur) {
      if (servisForm.islem_turu === 'Diğer' && !servisForm.diger_aciklama.trim()) {
        errs.diger_aciklama = 'Diğer işlem için açıklama zorunludur';
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    await onSave(form, servisForm);
  };

  const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-200 bg-white';
  const errorCls = 'text-xs text-red-500 mt-0.5';

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10 rounded-t-2xl">
          <h3 className="text-lg font-bold text-gray-800">{editData ? 'Müşteri Düzenle' : 'Yeni Müşteri Ekle'}</h3>
          <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer text-gray-500">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Ad Soyad */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Ad *</label>
              <input className={inputCls} value={form.ad} onChange={e => setForm(p => ({ ...p, ad: e.target.value }))} placeholder="Ahmet" />
              {errors.ad && <p className={errorCls}>{errors.ad}</p>}
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Soyad *</label>
              <input className={inputCls} value={form.soyad} onChange={e => setForm(p => ({ ...p, soyad: e.target.value }))} placeholder="Yılmaz" />
              {errors.soyad && <p className={errorCls}>{errors.soyad}</p>}
            </div>
          </div>

          {/* Telefon */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Telefon *</label>
              <input className={inputCls} value={form.telefon} onChange={e => setForm(p => ({ ...p, telefon: e.target.value }))} placeholder="0532 123 45 67" />
              {errors.telefon && <p className={errorCls}>{errors.telefon}</p>}
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Alternatif Telefon</label>
              <input className={inputCls} value={form.alternatif_telefon} onChange={e => setForm(p => ({ ...p, alternatif_telefon: e.target.value }))} placeholder="0533 123 45 67" />
            </div>
          </div>

          {/* Adres */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Açık Adres *</label>
            <textarea className={`${inputCls} resize-none`} rows={2} value={form.acik_adres} onChange={e => setForm(p => ({ ...p, acik_adres: e.target.value }))} placeholder="Mahalle, cadde, sokak, bina no, daire..." />
            {errors.acik_adres && <p className={errorCls}>{errors.acik_adres}</p>}
          </div>

          {/* İl / İlçe */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">İl</label>
              <select className={inputCls} value={form.il} onChange={e => setForm(p => ({ ...p, il: e.target.value }))}>
                <option value="">Seçiniz</option>
                {illerData.map(il => <option key={il} value={il}>{il}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">İlçe</label>
              <input className={inputCls} value={form.ilce} onChange={e => setForm(p => ({ ...p, ilce: e.target.value }))} placeholder="İlçe adı" />
            </div>
          </div>

          {/* Adres Tarifi */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Adres Tarifi</label>
            <input className={inputCls} value={form.adres_tarifi} onChange={e => setForm(p => ({ ...p, adres_tarifi: e.target.value }))} placeholder="Örn: Migros'un karşısı, mavi bina..." />
          </div>

          {/* Google Maps */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Google Harita Konum Bağlantısı</label>
            <input className={inputCls} value={form.google_maps_url} onChange={e => setForm(p => ({ ...p, google_maps_url: e.target.value }))} placeholder="https://maps.google.com/..." />
          </div>

          {/* Müşteri Durumu */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Müşteri Durumu</label>
            <select className={inputCls} value={form.musteri_durumu} onChange={e => setForm(p => ({ ...p, musteri_durumu: e.target.value }))}>
              {musteriDurumlari.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>

          {/* Not */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Genel Notlar</label>
            <textarea className={`${inputCls} resize-none`} rows={3} value={form.genel_notlar} onChange={e => setForm(p => ({ ...p, genel_notlar: e.target.value }))} placeholder="Özel notlar..." maxLength={500} />
            <p className="text-xs text-gray-400 mt-1 text-right">{form.genel_notlar.length}/500</p>
          </div>

          {/* ─── İlk İşlem / Servis Bilgisi ─── */}
          {!editData && (
            <div className="border-t border-gray-200 pt-5 mt-2">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                  <i className="ri-tools-line text-sky-600"></i>
                  İlk İşlem / Servis Bilgisi
                </h4>
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-xs text-gray-500">İşlem Kaydı Oluşturulsun mu?</span>
                  <button
                    type="button"
                    onClick={() => setServisForm(p => ({ ...p, islem_kaydi_olustur: !p.islem_kaydi_olustur }))}
                    className={`relative w-10 h-5 rounded-full transition-colors ${servisForm.islem_kaydi_olustur ? 'bg-sky-600' : 'bg-gray-300'}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${servisForm.islem_kaydi_olustur ? 'left-5' : 'left-0.5'}`}></span>
                  </button>
                </label>
              </div>

              {servisForm.islem_kaydi_olustur && (
                <div className="space-y-4 bg-sky-50/50 rounded-xl p-4 border border-sky-100">
                  {/* İşlem Türü */}
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">İşlem Türü</label>
                    <select
                      className={inputCls}
                      value={servisForm.islem_turu}
                      onChange={e => setServisForm(p => ({ ...p, islem_turu: e.target.value }))}
                    >
                      {ISLEM_TURLERI.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>

                  {servisForm.islem_turu === 'Diğer' && (
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Diğer İşlem Açıklaması *</label>
                      <input
                        className={inputCls}
                        value={servisForm.diger_aciklama}
                        onChange={e => setServisForm(p => ({ ...p, diger_aciklama: e.target.value }))}
                        placeholder="İşlemi açıklayın..."
                      />
                      {errors.diger_aciklama && <p className={errorCls}>{errors.diger_aciklama}</p>}
                    </div>
                  )}

                  {/* Tarih / Saat */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Servis Tarihi</label>
                      <input type="date" className={inputCls} value={servisForm.servis_tarihi} onChange={e => setServisForm(p => ({ ...p, servis_tarihi: e.target.value }))} />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Servis Saati</label>
                      <select className={inputCls} value={servisForm.servis_saati} onChange={e => setServisForm(p => ({ ...p, servis_saati: e.target.value }))}>
                        {saatler.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Ücret / Ödeme Durumu / Ödeme Tipi */}
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Ücret (₺)</label>
                      <input type="number" min="0" className={inputCls} value={servisForm.tahmini_ucret} onChange={e => setServisForm(p => ({ ...p, tahmini_ucret: e.target.value }))} placeholder="0" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Ödeme Durumu</label>
                      <select className={inputCls} value={servisForm.odeme_durumu} onChange={e => setServisForm(p => ({ ...p, odeme_durumu: e.target.value }))}>
                        {ODEME_DURUMLARI.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Ödeme Tipi</label>
                      <select className={inputCls} value={servisForm.odeme_tipi} onChange={e => setServisForm(p => ({ ...p, odeme_tipi: e.target.value }))}>
                        {ODEME_TIPLERI.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Personel */}
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Atanacak Personel</label>
                    <select className={inputCls} value={servisForm.personel_id} onChange={e => setServisForm(p => ({ ...p, personel_id: e.target.value }))}>
                      <option value="">Seçiniz...</option>
                      {personeller.map(p => <option key={p.id} value={p.id}>{p.ad} {p.soyad}</option>)}
                    </select>
                  </div>

                  {/* İşlem Açıklaması */}
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">İşlem Açıklaması</label>
                    <input className={inputCls} value={servisForm.islem_aciklamasi} onChange={e => setServisForm(p => ({ ...p, islem_aciklamasi: e.target.value }))} placeholder="Yapılacak işlem..." />
                  </div>

                  {/* Servis Notu */}
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Servis Notu</label>
                    <textarea className={`${inputCls} resize-none`} rows={2} value={servisForm.servis_notu} onChange={e => setServisForm(p => ({ ...p, servis_notu: e.target.value }))} placeholder="Ek notlar..." maxLength={500} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} disabled={saving} className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-semibold rounded-xl hover:bg-gray-50 transition-colors cursor-pointer text-sm whitespace-nowrap">
              İptal
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 py-2.5 bg-sky-600 hover:bg-sky-700 text-white font-semibold rounded-xl transition-colors cursor-pointer text-sm whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed">
              {saving ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                  Kaydediliyor...
                </span>
              ) : (
                'Kaydet'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}